import React, { useEffect, useMemo, useState } from 'react';
import { content, SupportedLocale } from '../lib/content';

type HeaderProps = {
  locale?: SupportedLocale;
  onLocaleChange?: (locale: SupportedLocale) => void;
  activeSection?: string;
  isBlogPage?: boolean;
};

export const Header: React.FC<HeaderProps> = ({
  locale = 'pl',
  onLocaleChange,
  activeSection,
  isBlogPage = false,
}) => {
  const t = content[locale];
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const brandLogo = isScrolled || menuOpen ? '/Logo.png' : '/Logo_black.png';

  /* ===========================
     SCROLL / MENU SIDE EFFECTS
     =========================== */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 80);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  /* ===========================
     HELPERS
     =========================== */
  const handleNavClick = () => setMenuOpen(false);

  const handleLocaleClick = (next: SupportedLocale) => {
    onLocaleChange?.(next);
    setMenuOpen(false);
  };

  const isHome =
    window.location.pathname === '/' || window.location.pathname === '';

  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  const resolveHref = (itemHref: string, type?: string) => {
    if (type === 'path') return itemHref;
    return isHome ? `#${itemHref}` : `/#${itemHref}`;
  };

  /* ===========================
     LABELS
     =========================== */
  const labelsByLocale: Record<
    SupportedLocale,
    {
      brandSingleLine: string;
      navAria: string;
      callLabelDesktop: string;
      callLabelMobile: string;
      callAria: string;
      mobileMenuAria: string;
      languageAria: string;
    }
  > = {
    pl: {
      brandSingleLine: 'Pieńkowski – Kancelaria Adwokacka',
      navAria: 'Główna nawigacja',
      callLabelDesktop: '+48 889 054 555',
      callLabelMobile: 'Zadzwoń',
      callAria: 'Zadzwoń: +48 889 054 555',
      mobileMenuAria: 'Otwórz menu',
      languageAria: 'Wybierz język',
    },
    en: {
      brandSingleLine: 'Pieńkowski – Law Office',
      navAria: 'Main navigation',
      callLabelDesktop: '+48 889 054 555',
      callLabelMobile: 'Call',
      callAria: 'Call: +48 889 054 555',
      mobileMenuAria: 'Open menu',
      languageAria: 'Choose language',
    },
    es: {
      brandSingleLine: 'Pieńkowski – Despacho de Abogados',
      navAria: 'Navegación principal',
      callLabelDesktop: '+48 889 054 555',
      callLabelMobile: 'Llamar',
      callAria: 'Llamar: +48 889 054 555',
      mobileMenuAria: 'Abrir menú',
      languageAria: 'Elegir idioma',
    },
  };

  const labels = labelsByLocale[locale];
  const callLabel = isMobile ? labels.callLabelMobile : labels.callLabelDesktop;

  const localeCodes = useMemo(
    () => ['pl', 'en', 'es'] as SupportedLocale[],
    []
  );

  /* ===========================
     RENDER
     =========================== */
  return (
    <header
      className={`site-header locale-${locale} ${
        isScrolled ? 'is-scrolled' : ''
      } ${menuOpen ? 'menu-open' : ''}`}
    >
      <div className="container header-inner">
        {/* BRAND */}
        <a className="brand" href="/">
          <div className="brand-mark" aria-hidden="true">
            <img src={brandLogo} alt="" />
          </div>
          <div className="brand-text brand-text--single">
            <span className="brand-line brand-line--single">
  <span className="brand-line__first">{labels.brandSingleLine.split('–')[0].trim()} –</span>{' '}
  <span className="brand-line__second">{labels.brandSingleLine.split('–').slice(1).join('–').trim()}</span>
</span>
          </div>
        </a>

        {/* DESKTOP NAV */}
        <nav className="desktop-nav" aria-label={labels.navAria}>
          <ul className="nav-list">
            {t.nav.map((item) => (
              <li key={item.href}>
                <a
                  className={`nav-link ${
                    (item.type === 'path' && isBlogPage) ||
                    (item.type !== 'path' && item.href === activeSection)
                      ? 'is-active'
                      : ''
                  }`}
                  href={resolveHref(item.href, item.type)}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="header-meta">
            <div className="locale-switch">
              {localeCodes.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={`locale-btn ${code === locale ? 'is-active' : ''}`}
                  onClick={() => handleLocaleClick(code)}
                  aria-pressed={code === locale}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>

            <a
              className="btn btn-outline header-cta"
              href="tel:+48889054555"
              aria-label={labels.callAria}
            >
              {callLabel}
            </a>
          </div>
        </nav>

        {/* MOBILE ACTIONS: language + hamburger */}
        <div className="mobile-actions">
          <button
            className={`icon-button hamburger ${menuOpen ? 'is-open' : ''}`}
            aria-expanded={menuOpen}
            aria-label={labels.mobileMenuAria}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div className={`mobile-menu ${menuOpen ? 'is-open' : ''}`}>
        <div className="mobile-menu-inner">
          <div className="mobile-nav-scroll">
            <ul className="mobile-nav-list">
              {t.nav.map((item) => (
                <li key={item.href}>
                  <a
                    className={`nav-link ${
                      (item.type === 'path' && isBlogPage) ||
                      (item.type !== 'path' && item.href === activeSection)
                        ? 'is-active'
                        : ''
                    }`}
                    href={resolveHref(item.href, item.type)}
                    onClick={handleNavClick}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>


          <div className="mobile-locale-row" aria-label={labels.languageAria}>
            <div className="locale-switch locale-switch--mobile">
              {localeCodes.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={`locale-btn ${code === locale ? 'is-active' : ''}`}
                  onClick={() => handleLocaleClick(code)}
                  aria-pressed={code === locale}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile call button */}
          <div className="mobile-meta">
            <a
              className="btn btn-outline header-cta mobile-call"
              href="tel:+48889054555"
              aria-label={labels.callAria}
            >
              {labels.callLabelMobile}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};
